import React, { useEffect, useState } from 'react';
import { useTheme } from '../../App';
import { useNavigate, useLocation } from 'react-router-dom';
import BackButton from '../../components/common/BackButton/BackButton';
import { getSettings, getFooterSettings } from '../../services/settingsCache';
import { apiClient } from '../../services/apiClient';
import { motion } from 'framer-motion';
import './Services.css';

const DEFAULT_SERVICES = [
  {
    title: 'Modular Kitchen',
    desc: 'Custom modular kitchen solutions tailored to your space and style preferences',
    points: ['3D Design', 'Quality Materials', 'Expert Installation'],
    iconKey: 'modular-kitchen',
  },
  {
    title: 'Interior Design',
    desc: 'Complete interior design and consultation services for residential and commercial spaces',
    points: ['Design Consultation', 'Space Planning', 'Material Selection'],
    iconKey: 'interior-design',
  },
  {
    title: 'False Ceiling',
    desc: 'Modern false ceiling designs with expert installation and finishing',
    points: ['Gypsum Work', 'POP Designs', 'LED Integration'],
    iconKey: 'false-ceiling',
  },
  {
    title: 'Carpentry Services',
    desc: 'Expert carpentry work for custom furniture and fixtures',
    points: ['Custom Furniture', 'Wardrobes', 'TV Units'],
    iconKey: 'carpentry',
  },
  {
    title: 'Painting Services',
    desc: 'Professional painting services with premium finishes and textures',
    points: ['Wall Painting', 'Texture Work', 'Waterproofing'],
    iconKey: 'painting',
  },
  {
    title: 'Floor Finishes',
    desc: 'Quality flooring solutions including tiles, wood, and vinyl',
    points: ['Tile Installation', 'Wood Flooring', 'Polishing'],
    iconKey: 'floor-finishes',
  },
  {
    title: 'Plumbing & Electrical',
    desc: 'Complete plumbing and electrical solutions for all requirements',
    points: ['Installation', 'Repairs', 'Maintenance'],
    iconKey: 'plumbing-electrical',
  },
  {
    title: 'Glass & Aluminum',
    desc: 'Custom glass work and aluminum fabrication for modern aesthetics',
    points: ['Partitions', 'Windows', 'Mirrors'],
    iconKey: 'glass-aluminum',
  },
];

const ICON_INDEX_BY_KEY = {
  'modular-kitchen': 0,
  'interior-design': 1,
  'false-ceiling': 2,
  carpentry: 3,
  painting: 4,
  'floor-finishes': 5,
  'plumbing-electrical': 6,
  'glass-aluminum': 7,
};

export default function Services() {
  const defaultBgUrl = 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1920&q=80';
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToServicesBanner = () => {
    const bannerElem = document.getElementById('services-banner');
    if (bannerElem) {
      const headerOffset = 80;
      const elementPosition = bannerElem.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: Math.max(0, offsetPosition),
        behavior: 'smooth'
      });
    } else {
      navigate('/services#services-banner');
    }
  };

  useEffect(() => {
    if (window.location.hash === '#services-banner' || location.hash === '#services-banner') {
      const timer = setTimeout(() => {
        const bannerElem = document.getElementById('services-banner');
        if (bannerElem) {
          const headerOffset = 80;
          const elementPosition = bannerElem.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          window.scrollTo({
            top: Math.max(0, offsetPosition),
            behavior: 'smooth'
          });
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, []);
  const renderIcon = (i) => {
    const common = {
      width: 24,
      height: 24,
      viewBox: '0 0 24 24',
      fill: 'none',
      stroke: '#16a34a',
      strokeWidth: 2,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      'aria-hidden': true,
    };
    switch (i) {
      case 0: // Home / Modular Kitchen
        return (
          <svg {...common}>
            <path d="M3 10.5l9-7 9 7" />
            <path d="M5 10.5V20a2 2 0 0 0 2 2h3v-6h4v6h3a2 2 0 0 0 2-2v-9.5" />
          </svg>
        );
      case 1: // Interior Design - Lightbulb/Idea
        return (
          <svg {...common}>
            <path d="M9 18h6" />
            <path d="M10 22h4" />
            <path d="M2 11a10 10 0 0 1 20 0c0 3.53-1.77 5.38-3.2 6.5-.86.66-1.8 1.13-1.8 2.5h-10c0-1.37-.94-1.84-1.8-2.5C3.77 16.38 2 14.53 2 11Z" />
          </svg>
        );
      case 2: // False Ceiling - Cube/Grid
        return (
          <svg {...common}>
            <rect x="3" y="3" width="18" height="18" rx="3" />
            <path d="M3 9h18M9 21V3" />
          </svg>
        );
      case 3: // Carpentry - Tool
        return (
          <svg {...common}>
            <path d="M14.7 6.3a4 4 0 0 1 5 5L12 19l-5-5 7.7-7.7Z" />
            <path d="M2 22l6-6" />
          </svg>
        );
      case 4: // Painting - Brush
        return (
          <svg {...common}>
            <path d="M2 22s3-1 5-3 3-5 3-5l7-7a3 3 0 1 1 4 4l-7 7s-3 1-5 3-3 5-3 5Z" />
            <path d="M14 7l3 3" />
          </svg>
        );
      case 5: // Floor Finishes - Layers
        return (
          <svg {...common}>
            <polygon points="12 2 2 7 12 12 22 7 12 2" />
            <polyline points="2 17 12 22 22 17" />
            <polyline points="2 12 12 17 22 12" />
          </svg>
        );
      case 6: // Plumbing & Electrical - Wrench
        return (
          <svg {...common}>
            <path d="M14.7 6.3a4 4 0 0 1 4.6 4.6L12 18.3l-3.6-3.6L14.7 6.3Z" />
            <path d="M2 22l6-6" />
          </svg>
        );
      case 7: // Glass & Aluminum - Cube
      default:
        return (
          <svg {...common}>
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
            <path d="M3.3 7L12 12l8.7-5" />
            <path d="M7 12l10 6" />
            <path d="M7 6l10 6" />
          </svg>
        );
    }
  };
  const { isNightMode } = useTheme();
  const allowFallbackContent = process.env.NODE_ENV !== 'production';
  const [heroBadge, setHeroBadge] = useState('Professional Services');
  const [heroTitle, setHeroTitle] = useState(
    'Expert Solutions For Your Construction Needs',
  );
  const [heroSubtitle, setHeroSubtitle] = useState(
    'From design consultation to complete project execution, we provide end-to-end services for residential and commercial spaces.',
  );
  const [heroButtonText, setHeroButtonText] = useState('Request Consultation');
  const [heroButtonHref, setHeroButtonHref] = useState('/contact');
  const [heroImageUrl, setHeroImageUrl] = useState('');
  const [services, setServices] = useState([]);
  const [footerSettings, setFooterSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedServiceDetails, setSelectedServiceDetails] = useState(null);
  const [error, setError] = useState('');
  const bgUrl = heroImageUrl || defaultBgUrl;

  const goToContact = () => navigate('/contact');

  useEffect(() => {
    let cancelled = false;

    const loadContent = async () => {
      try {
        // Load footer settings for global contact info
        const foot = await getFooterSettings().catch(() => null);
        if (!cancelled && foot) {
          setFooterSettings(foot);
        }

        // Load services page content from content management API
        const servicesData = await apiClient.get('/content/services');
        if (!cancelled && servicesData) {
          setHeroBadge(prev => servicesData.heroBadge || prev || 'Professional Services');
          setHeroTitle(prev => servicesData.heroTitle || prev || 'Expert Solutions For Your Construction Needs');
          setHeroSubtitle(prev => servicesData.heroSubtitle || prev || 'From design consultation to complete project execution, we provide end-to-end services for residential and commercial spaces.');
          setHeroButtonText(prev => servicesData.heroButtonText || prev || 'Request Consultation');
          setHeroButtonHref(prev => servicesData.heroButtonHref || prev || '/contact');
          setHeroImageUrl(prev => servicesData.heroImageUrl || prev || '');
          
          // Load services list if available
          if (servicesData.services && Array.isArray(servicesData.services)) {
            setServices(servicesData.services);
          }
        }

        // Fallback to general settings if content management doesn't have data
        const settingsData = await getSettings();
        if (!cancelled && settingsData) {
          setHeroBadge(prev => settingsData.servicesHeroBadge || prev);
          setHeroTitle(prev => settingsData.servicesHeroTitle || prev);
          setHeroSubtitle(prev => settingsData.servicesHeroSubtitle || prev);
          setHeroButtonText(prev => settingsData.servicesHeroButtonText || prev);
          setHeroButtonHref(prev => settingsData.servicesHeroButtonHref || prev);
          setHeroImageUrl(prev => settingsData.servicesHeroImageUrl || prev);
        }
      } catch (err) {
        console.error('Failed to load services content', err);
      }
    };

    loadContent();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const loadServices = async () => {
      try {
        setIsLoading(true);
        setError('');

        const data = await apiClient.get('/services');
        if (isCancelled) return;

        if (Array.isArray(data)) {
          setServices(data);
        }
      } catch (err) {
        console.error('Failed to load services', err);
        if (!isCancelled) {
          setError('Unable to load services from server. Showing default services.');
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    loadServices();

    return () => {
      isCancelled = true;
    };
  }, []);

  return (
    <main className="services-page" style={{ background: isNightMode ? '#020617' : '#ffffff' }}>
      {/* Service Details Modal */}
      {selectedServiceDetails && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
          backdropFilter: 'blur(3px)'
        }} onClick={() => setSelectedServiceDetails(null)}>
          <div style={{
            background: isNightMode ? '#1e293b' : '#ffffff',
            borderRadius: '16px', padding: '28px', maxWidth: '540px', width: '100%',
            position: 'relative', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)',
            maxHeight: '90vh', overflowY: 'auto'
          }} onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={() => setSelectedServiceDetails(null)}
              style={{
                position: 'absolute', top: '16px', right: '16px', background: 'none',
                border: 'none', fontSize: '26px', cursor: 'pointer', lineHeight: 1,
                color: isNightMode ? '#94a3b8' : '#6b7280'
              }}
            >&times;</button>
            <h2 style={{ marginTop: 0, marginBottom: '16px', fontSize: '24px', fontWeight: 700, color: isNightMode ? '#f8fafc' : '#111827' }}>
              {selectedServiceDetails.title}
            </h2>
            {selectedServiceDetails.imgSrc && (
              <img 
                src={selectedServiceDetails.imgSrc} 
                alt={selectedServiceDetails.title}
                style={{ width: '100%', height: '220px', objectFit: 'cover', borderRadius: '10px', marginBottom: '20px' }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80';
                }}
              />
            )}
            <p style={{ color: isNightMode ? '#cbd5e1' : '#4b5563', lineHeight: 1.6, marginBottom: '20px', fontSize: '15px' }}>
              {selectedServiceDetails.desc}
            </p>
            {selectedServiceDetails.points && Array.isArray(selectedServiceDetails.points) && selectedServiceDetails.points.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ margin: '0 0 10px', fontSize: '15px', fontWeight: 600, color: isNightMode ? '#f1f5f9' : '#1e293b' }}>Key Service Features:</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 10 }}>
                  {selectedServiceDetails.points.map((pt, i) => (
                    <li key={i} style={{ color: isNightMode ? '#e2e8f0' : '#374151', display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: '14px', lineHeight: 1.4 }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <span>{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingTop: '16px', borderTop: `1px solid ${isNightMode ? '#334155' : '#f1f5f9'}` }}>
              <button 
                onClick={() => setSelectedServiceDetails(null)}
                style={{
                  padding: '10px 20px', borderRadius: '8px', background: 'transparent',
                  border: `1px solid ${isNightMode ? '#475569' : '#d1d5db'}`,
                  color: isNightMode ? '#e2e8f0' : '#374151', cursor: 'pointer', fontWeight: 600
                }}
              >Close</button>
              <button 
                onClick={() => { setSelectedServiceDetails(null); navigate('/contact'); }}
                style={{
                  padding: '10px 22px', borderRadius: '8px', background: '#16a34a',
                  border: 'none', color: 'white', cursor: 'pointer', fontWeight: 600
                }}
              >Contact Us Now</button>
            </div>
          </div>
        </div>
      )}
 
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '12px 20px 0' }}>
        <BackButton fallbackTo="/" />
      </div>

    <motion.section
      id="services-banner"
      className="services-hero"
      style={{
        backgroundImage: `url(${bgUrl})`,
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(90deg, rgba(17,24,39,0.75) 0%, rgba(17,24,39,0.45) 55%, rgba(17,24,39,0.15) 100%)',
        }}
      />
      <motion.div style={{ position: 'relative', zIndex: 1, width: '100%' }}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 20px', color: '#fff' }}>
          <motion.div style={{
            display: 'inline-block',
            padding: '6px 12px',
            borderRadius: 9999,
            background: '#A7E08D',
            color: '#000',
            fontSize: 12,
            marginBottom: 14,
            textAlign: 'left',
          }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: 0.4 }}
          >
            {heroBadge}
          </motion.div>
          <motion.h1 style={{ fontSize: 48, lineHeight: 1.05, margin: '8px 0 16px', textShadow: '0 2px 6px rgba(0,0,0,0.35)', textAlign: 'left' }}
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            {heroTitle}
          </motion.h1>
          <motion.p style={{ maxWidth: 680, margin: '0 0 0', textShadow: '0 1px 4px rgba(0,0,0,0.3)', textAlign: 'left' }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 0.9, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            {heroSubtitle}
          </motion.p>
          <motion.div style={{ marginTop: 16, textAlign: 'left' }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
          >
            <motion.a
              href={heroButtonHref || '/contact'}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 40,
                padding: '0 16px',
                borderRadius: 10,
                background: '#22c55e',
                color: '#052e16',
                fontWeight: 600,
                textDecoration: 'none',
                border: '1px solid #22c55e',
              }}
              onClick={(e) => {
                e.preventDefault();
                goToContact();
              }}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
            >
              {heroButtonText}
            </motion.a>
          </motion.div>
        </div>
      </motion.div>
    </motion.section>

      {/* Our Services grid */}
      <motion.section aria-labelledby="services-grid-title" style={{ padding: '48px 0 72px', background: isNightMode ? '#1a1a1a' : '#fff' }}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.6 }}
      >
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 20px' }}>
          <motion.h2 id="services-grid-title" style={{ textAlign: 'center', margin: 0 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >Our Services</motion.h2>
          <motion.p style={{ textAlign: 'center', color: isNightMode ? '#e5e7eb' : '#6b7280', margin: '8px 0 28px' }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            Comprehensive construction and interior solutions with expert craftsmanship and quality materials
          </motion.p>
          {isLoading && (
            <p style={{ textAlign: 'center', color: isNightMode ? '#e5e7eb' : '#6b7280', margin: '0 0 16px' }}>
              Loading services...
            </p>
          )}
          {error && !isLoading && (
            <p style={{ textAlign: 'center', color: '#dc2626', margin: '0 0 16px' }}>
              {error}
            </p>
          )}
          <div
            className="services-grid"
          >
            {((services.length || allowFallbackContent) ? (services.length ? services : DEFAULT_SERVICES) : []).map((item, idx) => {
              const iconKey = item.iconKey || item.slug || '';
              const mappedIndex =
                iconKey && ICON_INDEX_BY_KEY[iconKey] !== undefined
                  ? ICON_INDEX_BY_KEY[iconKey]
                  : idx;
              const points =
                Array.isArray(item.highlights) && item.highlights.length
                  ? item.highlights
                  : Array.isArray(item.points)
                  ? item.points
                  : [];
              const title = item.name || item.title;
              const desc = item.description || item.desc;

              return (
                <motion.article
                  key={item.id || title || idx}
                  onClick={() => {
                    setSelectedServiceDetails({
                      title: title,
                      desc: desc,
                      imgSrc: item.imageUrl || item.heroImageUrl || defaultBgUrl,
                      points: points
                    });
                  }}
                  style={{
                    background: isNightMode ? '#1a1a1a' : '#fff',
                    color: isNightMode ? '#e5e7eb' : '#111827',
                    borderRadius: 16,
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                    padding: 22,
                  }}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  whileHover={{ scale: 1.03, y: -5 }}
                >
                  <div>
                    <div
                      aria-hidden="true"
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: 12,
                        background: 'rgba(34,197,94,0.15)',
                        color: '#16a34a',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 12,
                      }}
                    >
                      {renderIcon(mappedIndex)}
                    </div>
                    <h3 style={{ margin: '0 0 6px', fontSize: 18, color: isNightMode ? '#e5e7eb' : '#111827' }}>{title}</h3>
                    <p
                      style={{
                        margin: 0,
                        color: isNightMode ? '#e5e7eb' : '#4A5565',
                        fontFamily: 'Arial, sans-serif',
                        fontSize: 14,
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '20px',
                      }}
                    >
                      {desc}
                    </p>
                    <ul style={{ listStyle: 'none', padding: 0, margin: '12px 0 0', display: 'grid', gap: 8 }}>
                      {points.map((p, i) => (
                        <li key={i} style={{ color: isNightMode ? '#e5e7eb' : '#374151', display: 'flex', alignItems: 'center', gap: 8 }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <circle cx="12" cy="12" r="9" />
                            <path d="M8 12l2.5 2.5L16 9" />
                          </svg>
                          {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </motion.section>

      {/* Our Working Process */}
      <section style={{ padding: '60px 20px', background: isNightMode ? '#1a1a1a' : '#f9fafb' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <h2 id="working-process-title" style={{ textAlign: 'center', margin: 0 }}>Our Working Process</h2>
          <p style={{ textAlign: 'center', color: isNightMode ? '#e5e7eb' : '#6b7280', margin: '8px auto 48px', maxWidth: 600 }}>
            Simple, transparent, and efficient workflow to bring your vision to life
          </p>
          
          <div style={{ position: 'relative', margin: '0 auto', maxWidth: 1000 }}>
            {/* Connecting line */}
            <div style={{
              position: 'absolute',
              top: 32,
              left: '10%',
              right: '10%',
              height: 2,
              background: isNightMode ? '#e5e7eb' : '#e5e7eb',
              zIndex: 1
            }}></div>
            
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              position: 'relative',
              zIndex: 2
            }}>
              {[
                {
                  number: '01',
                  title: 'Consultation',
                  description: 'Free site visit and requirement analysis'
                },
                {
                  number: '02',
                  title: 'Design',
                  description: '3D visualization and material selection'
                },
                {
                  number: '03',
                  title: 'Quotation',
                  description: 'Transparent pricing with detailed breakdown'
                },
                {
                  number: '04',
                  title: 'Execution',
                  description: 'Professional installation by expert team'
                },
                {
                  number: '05',
                  title: 'Handover',
                  description: 'Quality check and project completion'
                }
              ].map((step, index) => (
                <div key={index} style={{ textAlign: 'center', position: 'relative', width: '20%' }}>
                  <div style={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: isNightMode ? '#10b981' : '#10b981',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 20,
                    fontWeight: 600,
                    margin: '0 auto 16px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                  }}>
                    {step.number}
                  </div>
                  <h3 style={{ 
                    fontSize: 18, 
                    fontWeight: 600, 
                    margin: '0 0 8px',
                    color: isNightMode ? '#e5e7eb' : '#1f2937'
                  }}>
                    {step.title}
                  </h3>
                  <p style={{
                    color: isNightMode ? '#e5e7eb' : '#6b7280',
                    fontSize: 14,
                    lineHeight: '1.5',
                    maxWidth: 180,
                    margin: '0 auto'
                  }}>
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Bulk Material Supply */}
      <section
        className="bulk-section"
        style={{ padding: '80px 20px', background: isNightMode ? '#1a1a1a' : '#fff' }}
      >
        <div className="bulk-section__inner">
          <div className="bulk-section__content">
            <div
              style={{
                display: 'inline-block',
                padding: '6px 12px',
                borderRadius: 9999,
                background: '#A7E08D',
                color: '#000',
                fontSize: 12,
                marginBottom: 10,
              }}
            >
              For Contractors & Builders
            </div>
            <h2 style={{ fontSize: 36, color: isNightMode ? '#e5e7eb' : '#1f2937', margin: '0 0 20px' }}>Bulk Material Supply</h2>
            <p style={{ color: isNightMode ? '#e5e7eb' : '#4b5563', lineHeight: 1.6, marginBottom: 24, fontSize: 16 }}>
              Specialized material supply solutions for contractors and builders with competitive pricing and reliable delivery.
            </p>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 12, marginBottom: 32 }}>
              {[
                'Volume-based pricing for bulk orders',
                'Dedicated account manager for all your needs',
                'Scheduled deliveries to keep your project on track',
                'Credit facilities for registered contractors',
                'Quality assurance with all necessary certifications'
              ].map((item, index) => (
                <li key={index} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ minWidth: 24, color: isNightMode ? '#10b981' : '#10b981' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                  </div>
                  <span style={{ color: isNightMode ? '#e5e7eb' : '#4b5563' }}>{item}</span>
                </li>
              ))}
            </ul>
            <button style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              height: 44,
              padding: '0 20px',
              borderRadius: 6,
              background: isNightMode ? '#000000' : '#000000',
              color: 'white',
              fontWeight: 500,
              border: 'none',
              cursor: 'pointer',
              fontSize: 15,
              transition: 'all 0.2s ease',
            }} 
              onMouseOver={(e) => {
                e.currentTarget.style.opacity = '0.9';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.opacity = '1';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              onClick={goToContact}
            >
              Request Bulk Quote
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </button>
          </div>
          <div
            className="bulk-section__media"
            style={{ borderRadius: 12, overflow: 'hidden', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' }}
          >
            <img 
              src={`${process.env.PUBLIC_URL}/assets/images/ImageWithFallback.png`} 
              alt="Bulk Material Supply Warehouse" 
              style={{ width: '100%', height: 'auto', display: 'block' }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80';
              }}
            />
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section style={{ padding: '80px 20px', background: isNightMode ? '#1a1a1a' : '#f9fafb' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div className="additional-services-grid">
            {/* Card 1 */}
            <div 
              style={{
                background: isNightMode ? '#1a1a1a' : '#fff',
                borderRadius: 12,
                overflow: 'hidden',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                transform: 'translateY(0)'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
              }}
            >
              <img 
                src={`${process.env.PUBLIC_URL}/assets/images/p1.png`} 
                alt="Site Planning & Consultation"
                style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80';
                }}
              />
              <div style={{ padding: '24px' }}>
                <h3 style={{ margin: '0 0 12px', fontSize: '20px', color: isNightMode ? '#e5e7eb' : '#111827' }}>Site Planning & Consultation</h3>
                <p style={{ margin: '0 0 20px', color: isNightMode ? '#e5e7eb' : '#4b5563', lineHeight: 1.6, fontSize: '15px' }}>
                  Expert guidance on materials selection, usage estimation, and cost optimization for your project.
                </p>
                <button 
                  style={{
                    padding: '10px 20px',
                    borderRadius: '6px',
                    background: isNightMode ? '#f3f4f6' : '#f3f4f6',
                    color: isNightMode ? '#374151' : '#374151',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: '14px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = isNightMode ? '#e5e7eb' : '#e5e7eb'}
                  onMouseOut={(e) => e.currentTarget.style.background = isNightMode ? '#f3f4f6' : '#f3f4f6'}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedServiceDetails({
                      title: 'Site Planning & Consultation',
                      desc: 'Expert guidance on materials selection, usage estimation, and cost optimization for your project. Our technical team conducts site visits, detailed structural evaluation, material requirement planning, and budget optimization.',
                      imgSrc: `${process.env.PUBLIC_URL}/assets/images/p1.png`,
                      points: [
                        'Comprehensive site visit and measurement analysis',
                        'Material estimation and budget optimization',
                        'Architectural and structural compatibility review',
                        'Dedicated technical advisor for your project'
                      ]
                    });
                  }}
                >
                  Book Consultation
                </button>
              </div>
            </div>

            {/* Card 2 */}
            <div 
              style={{
                background: isNightMode ? '#1a1a1a' : '#fff',
                borderRadius: 12,
                overflow: 'hidden',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                transform: 'translateY(0)',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
              }}
            >
              <img 
                src={`${process.env.PUBLIC_URL}/assets/images/p2.png`} 
                alt="Custom Solutions" 
                style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80';
                }}
              />
              <div style={{ padding: '24px' }}>
                <h3 style={{ margin: '0 0 12px', fontSize: '20px', color: isNightMode ? '#e5e7eb' : '#111827' }}>Custom Solutions</h3>
                <p style={{ margin: '0 0 20px', color: isNightMode ? '#e5e7eb' : '#4b5563', lineHeight: 1.6, fontSize: '15px' }}>
                  Special manufacturing and custom orders for unique project requirements and specifications.
                </p>
                <button 
                  style={{
                    padding: '10px 20px',
                    borderRadius: '6px',
                    background: isNightMode ? '#f3f4f6' : '#f3f4f6',
                    color: isNightMode ? '#374151' : '#374151',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: '14px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = isNightMode ? '#e5e7eb' : '#e5e7eb'}
                  onMouseOut={(e) => e.currentTarget.style.background = isNightMode ? '#f3f4f6' : '#f3f4f6'}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedServiceDetails({
                      title: 'Custom Solutions',
                      desc: 'Special manufacturing and custom orders for unique project requirements and specifications. From custom-sized glass & aluminum fabrications to bespoke interior woodwork and specialized structural orders, we tailor solutions to match your exact design vision.',
                      imgSrc: `${process.env.PUBLIC_URL}/assets/images/p2.png`,
                      points: [
                        'Custom sizing and bespoke manufacturing',
                        'Architectural grade materials and specialized finishes',
                        'Direct coordination with manufacturing facilities',
                        'Flexible minimum order quantities'
                      ]
                    });
                  }}
                >
                  Learn More
                </button>
              </div>
            </div>

            {/* Card 3 */}
            <div 
              style={{
                background: isNightMode ? '#1a1a1a' : '#fff',
                borderRadius: 12,
                overflow: 'hidden',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                transform: 'translateY(0)',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
              }}
            >
              <img 
                src={`${process.env.PUBLIC_URL}/assets/images/p3.png`} 
                alt="Installation Support" 
                style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80';
                }}
              />
              <div style={{ padding: '24px' }}>
                <h3 style={{ margin: '0 0 12px', fontSize: '20px', color: isNightMode ? '#e5e7eb' : '#111827' }}>Installation Support</h3>
                <p style={{ margin: '0 0 20px', color: isNightMode ? '#e5e7eb' : '#4b5563', lineHeight: 1.6, fontSize: '15px' }}>
                  Professional installation services with experienced technicians for all your requirements.
                </p>
                <button 
                  style={{
                    padding: '10px 20px',
                    borderRadius: '6px',
                    background: isNightMode ? '#f3f4f6' : '#f3f4f6',
                    color: isNightMode ? '#374151' : '#374151',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: '14px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = isNightMode ? '#e5e7eb' : '#e5e7eb'}
                  onMouseOut={(e) => e.currentTarget.style.background = isNightMode ? '#f3f4f6' : '#f3f4f6'}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedServiceDetails({
                      title: 'Installation Support',
                      desc: 'Professional installation services with experienced technicians for all your requirements. Our certified technicians ensure precise, reliable, and safe installation for all building, interior, glass, sanitary, and structural materials.',
                      imgSrc: `${process.env.PUBLIC_URL}/assets/images/p3.png`,
                      points: [
                        'Certified & experienced installation technicians',
                        'Complete end-to-end fitting and assembly',
                        'Safety compliance and post-installation inspection',
                        'On-time project execution and service warranty'
                      ]
                    });
                  }}
                >
                  View Services
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Our Clients Say */}
      <section
        className="testimonials-section"
        style={{
          padding: "80px 20px",
          background: "linear-gradient(to bottom, #0A0F1F, #000000)",
          color: "#ffffff",
          textAlign: "center",
        }}
      >
        <div className="testimonials-inner">

    {/* Green Tag */}
    <div
      style={{
        display: "inline-block",
        backgroundColor: "#A8D08D",
        color: "#000000",
        padding: "6px 16px",
        borderRadius: "20px",
        fontSize: "14px",
        fontWeight: "600",
        marginBottom: "16px",
        letterSpacing: "0.05em",
      }}
    >
      Testimonials
    </div>

    {/* Title */}
    <h2
      style={{
        fontSize: "2.5rem",
        fontWeight: "700",
        marginBottom: "12px",
      }}
    >
      What Our Clients Say
    </h2>

    {/* Subtitle */}
    <p
      style={{
        fontSize: "1.125rem",
        color: "#9ca3af",
        maxWidth: "700px",
        margin: "0 auto 60px",
        lineHeight: "1.6",
      }}
    >
      Real experiences from our satisfied customers
    </p>

    {/* Testimonial Cards */}
    <div className="testimonials-grid">
      {/* Card 1 */}
      <div
        className="testimonial-card"
        style={{
          background: "#1B2434",
          padding: "32px",
          borderRadius: "12px",
          border: "1px solid rgba(255,255,255,0.08)",
          textAlign: "left",
        }}
      >
        <div style={{ color: "#FBBF24", fontSize: "20px", marginBottom: "16px" }}>
          ★★★★★
        </div>

        <p
          style={{
            fontSize: "1rem",
            color: "#E5E7EB",
            lineHeight: "1.7",
            marginBottom: "28px",
          }}
        >
          "Tiwari Syndicate provided exceptional service for our residential
          project. The quality of materials and professional installation
          exceeded our expectations. Highly recommended for anyone looking for
          reliable construction material suppliers."
        </p>

        <h4
          style={{
            fontSize: "1rem",
            fontWeight: "600",
            marginBottom: "4px",
            color: "#A8D08D",
          }}
        >
          Rajesh Kumar
        </h4>
        <p style={{ fontSize: "0.85rem", color: "#9ca3af" }}>
          Homeowner, Delhi
        </p>
      </div>

      {/* Card 2 */}
      <div
        className="testimonial-card"
        style={{
          background: "#1B2434",
          padding: "32px",
          borderRadius: "12px",
          border: "1px solid rgba(255,255,255,0.08)",
          textAlign: "left",
        }}
      >
        <div style={{ color: "#FBBF24", fontSize: "20px", marginBottom: "16px" }}>
          ★★★★★
        </div>

        <p
          style={{
            fontSize: "1rem",
            color: "#E5E7EB",
            lineHeight: "1.7",
            marginBottom: "28px",
          }}
        >
          "We've been working with Tiwari Syndicate for multiple commercial
          projects. Their bulk supply services, competitive pricing, and timely
          delivery have made them our preferred vendor. Great team to work with!"
        </p>

        <h4
          style={{
            fontSize: "1rem",
            fontWeight: "600",
            marginBottom: "4px",
            color: "#A8D08D",
          }}
        >
          Priya Sharma
        </h4>
        <p style={{ fontSize: "0.85rem", color: "#9ca3af" }}>
          Construction Manager, Mumbai
        </p>
      </div>
    </div>
  </div>
</section>


      {/* Ready to Start Your Project? */}
      <section
        className="ready-cta-section"
        style={{ 
          padding: "70px 20px",
          background: "linear-gradient(to bottom, #5D7F4E, #4A6640)", // green background from screenshot
          textAlign: "center",
          color: "#ffffff"
        }}
      >
        <div
          className="ready-cta__inner"
          style={{ 
            maxWidth: "700px",
            margin: "0 auto",
          }}
        >
          <h2 style={{ 
            fontSize: "32px",
            fontWeight: "600",
            marginBottom: "10px",
            color: "#ffffff",
          }}>
            Ready to Start Your Project?
          </h2>
          
           <p
      style={{
        fontSize: "16px",
        color: "#e4e4e4",
        marginBottom: "30px",
        lineHeight: "1.6",
      }}
    >
      Get in touch with our experts today for a free consultation and quote
    </p>

    {/* Buttons Row */}
    <div
      className="ready-cta__buttons"
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "20px",
      }}
    >
      {/* Phone Button */}
      <button
        style={{
          background: "#ffffff",
          color: "#000000",
          border: "none",
          padding: "12px 20px",
          borderRadius: "8px",
          fontSize: "15px",
          fontWeight: "500",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          cursor: "pointer",
        }}
        onClick={() => {
          const phone = footerSettings?.companyPrimaryPhone || '';
          if (phone) window.location.href = `tel:${phone.replace(/[^\d+]/g, '')}`;
        }}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#000000"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
        {footerSettings?.companyPrimaryPhone ? `Call: ${footerSettings.companyPrimaryPhone}` : 'Call Now'}
      </button>

      {/* Request Quote Button */}
      <button
        style={{
          background: "#1C2635", // deep blue from screenshot
          color: "#ffffff",
          padding: "12px 28px",
          borderRadius: "8px",
          fontSize: "15px",
          fontWeight: "500",
          border: "1px solid #1C2635",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}
        onClick={goToContact}
      >
        Request Quote
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="5" y1="12" x2="19" y2="12" />
          <polyline points="12 5 19 12 12 19" />
        </svg>
      </button>
    </div>
  </div>
</section>

    </main>
  );
}
